import React, { FC, useCallback, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import USERS, { getUserDataWithUsername } from '../../../common/data/userDummyData';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';
import { Authlogin, Authsignup } from '../../../services/auth';
import LogoLight from '../../../components/LogoLight';

interface ILoginHeaderProps {
	isNewUser?: boolean;
}
const LoginHeader: FC<ILoginHeaderProps> = ({ isNewUser }) => {
	if (isNewUser) {
		return (
			<>
				<div className='text-center h1 fw-bold mt-5'>Create Account,</div>
				<div className='text-center h4 text-muted mb-5'>Sign up to get started!</div>
			</>
		);
	}
	return (
		<>
			<div className='text-center h1 fw-bold mt-5'>Welcome,</div>
			<div className='text-center h4 text-muted mb-5'>Sign in to continue!</div>
		</>
	);
};

interface ILoginProps {
	isSignUp?: boolean;
}
const Login: FC<ILoginProps> = ({ isSignUp }) => {
	const { setUser } = useContext(AuthContext);
	const { darkModeStatus } = useDarkMode();
	const navigate = useNavigate();

	const [signInPassword, setSignInPassword] = useState<boolean>(false);
	const [singUpStatus, setSingUpStatus] = useState<boolean>(!!isSignUp);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [apiError, setApiError] = useState<string | null>(null);

	// Validation schema for login
	const loginValidationSchema = Yup.object().shape({
		email: Yup.string()
			.email('Invalid email address')
			.required('Email is required'),
		password: Yup.string()
			.required('Password is required')
			.min(6, 'Password must be at least 6 characters')
	});

	// Validation schema for signup
	const signupValidationSchema = Yup.object().shape({
		fName: Yup.string().required('First name is required'),
		lName: Yup.string().required('Last name is required'),
		email: Yup.string()
			.email('Invalid email address')
			.required('Email is required'),
		password: Yup.string()
			.required('Password is required')
			.min(6, 'Password must be at least 6 characters')
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: singUpStatus
			? { fName: '', lName: '', email: '', password: '' }
			: { email: '', password: '' },
		validationSchema: singUpStatus ? signupValidationSchema : loginValidationSchema,
		validateOnChange: false,
		onSubmit: async (values) => {
			setIsLoading(true);
			setApiError(null);

			try {
				let response;
				if (singUpStatus) {
					// Signup flow
					response = await Authsignup({
						fName: values.fName,
						lName: values.lName,
						email: values.email,
						password: values.password
					});
				} else {
					// Login flow
					response = await Authlogin({
						email: values.email,
						password: values.password
					});
				}

				// Check if there's an error in the response
				if (response.error) {
					setApiError(response.error);
					setIsLoading(false);
					return;
				}

				// Successful login/signup
				if (setUser) {
					setUser(response.data.admin.fName, response.data.admin);
					localStorage.setItem('facit_authToken', response.data.admin.token);
				}
				navigate('/');
			} catch (error) {
				setApiError('An unexpected error occurred');
			} finally {
				setIsLoading(false);
			}
		},
	});

	// Reset error when switching between login and signup
	const handleToggleMode = () => {
		setSignInPassword(false);
		setSingUpStatus(!singUpStatus);
		setApiError(null);
		formik.resetForm();
	};

	return (
		<PageWrapper
			isProtected={false}
			title={singUpStatus ? 'Sign Up' : 'Login'}
			className={classNames({ 'bg-dark': !singUpStatus, 'bg-light': singUpStatus })}>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-center my-5'>
									<Link
										to="/"
										className={classNames(
											'text-decoration-none fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}
										aria-label="Facit"
									>
										{darkModeStatus ? (
											<Logo width={200} /> // Use the dark mode logo component
										) : (
											<LogoLight width={200} /> // Use the light mode logo component
										)}
									</Link>

								</div>
								{apiError && (
									<Alert color='danger' isLight icon='Error' isDismissible>
										{apiError}
									</Alert>
								)}

								<form className='row g-4' onSubmit={formik.handleSubmit}>
									{singUpStatus ? (
										<>
											<div className='col-12'>
												<FormGroup
													id='fName'
													isFloating
													label='First Name'>
													<Input
														name='fName'
														value={formik.values.fName}
														onChange={formik.handleChange}
														isTouched={formik.touched.fName}
														invalidFeedback={formik.errors.fName}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='lName'
													isFloating
													label='Last Name'>
													<Input
														name='lName'
														value={formik.values.lName}
														onChange={formik.handleChange}
														isTouched={formik.touched.lName}
														invalidFeedback={formik.errors.lName}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='email'
													isFloating
													label='Email'>
													<Input
														type='email'
														name='email'
														value={formik.values.email}
														onChange={formik.handleChange}
														isTouched={formik.touched.email}
														invalidFeedback={formik.errors.email}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='password'
													isFloating
													label='Password'>
													<Input
														type='password'
														name='password'
														value={formik.values.password}
														onChange={formik.handleChange}
														isTouched={formik.touched.password}
														invalidFeedback={formik.errors.password}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<Button
													color='info'
													className='w-100 py-3'
													type='submit'
													isDisable={isLoading}>
													{isLoading ? <Spinner isSmall inButton isGrow /> : 'Sign Up'}
												</Button>
											</div>
										</>
									) : (
										<>
											<div className='col-12'>
												<FormGroup
													id='email'
													isFloating
													label='Email'>
													<Input
														type='email'
														name='email'
														value={formik.values.email}
														onChange={formik.handleChange}
														isTouched={formik.touched.email}
														invalidFeedback={formik.errors.email}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='password'
													isFloating
													label='Password'>
													<Input
														type='password'
														name='password'
														value={formik.values.password}
														onChange={formik.handleChange}
														isTouched={formik.touched.password}
														invalidFeedback={formik.errors.password}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<Button
													color='warning'
													className='w-100 py-3'
													type='submit'
													isDisable={isLoading}>
													{isLoading ? <Spinner isSmall inButton isGrow /> : 'Login'}
												</Button>
											</div>
										</>
									)}
									<div className="text-center mt-3">
										{singUpStatus ? (
											<p>
												Already have an account?{' '}
												<Button
													color="link"
													className="p-0"
													onClick={handleToggleMode}
													style={{ textDecoration: 'underline' }}
												>
													Login here
												</Button>
											</p>
										) : (
											<p>
												Don't have an account?{' '}
												<Button
													color="link"
													className="p-0"
													onClick={handleToggleMode}
													style={{ textDecoration: 'underline' }}
												>
													Sign up here
												</Button>
											</p>
										)}
									</div>
									{/* Mode Toggle Buttons */}
									{/* <div className='col-12 mt-3'>
                                        <div className='row row-cols-2 g-3'>
                                            <div className='col'>
                                                <Button
                                                    color={darkModeStatus ? 'light' : 'dark'}
                                                    isLight={singUpStatus}
                                                    className='rounded-1 w-100'
                                                    size='lg'
                                                    onClick={handleToggleMode}>
                                                    Login
                                                </Button>
                                            </div>
                                            <div className='col'>
                                                <Button
                                                    color={darkModeStatus ? 'light' : 'dark'}
                                                    isLight={!singUpStatus}
                                                    className='rounded-1 w-100'
                                                    size='lg'
                                                    onClick={handleToggleMode}>
                                                    Sign Up
                                                </Button>
                                            </div>
                                        </div>
                                    </div> */}
								</form>
							</CardBody>
						</Card>
						<div className='text-center'>
							<a
								href='/'
								className={classNames('text-decoration-none me-3', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}>
								Privacy policy
							</a>
							<a
								href='/'
								className={classNames('link-light text-decoration-none', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}>
								Terms of use
							</a>
						</div>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Login;
